/**
 * SPEC-AgentUI / SPEC-AgentDetail-Revisions — ClaimToast.
 *
 * Originally rendered four variants (warn / success / released / crit). The
 * 2026-05-29 Agent Detail revisions collapse the surface to a single off-page
 * "Expiring Soon" popup — Frame 4 of the mockup. The three dead variants
 * (success, released, crit) are gone; only `warn` remains. Component prop
 * surface is unchanged so callers don't have to change shape, only stop
 * passing the dead literals.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Starling } from "@/lib/starling-brand";

const TOKENS = {
  card: "#FFFFFF",
  border: "#E5E7EB",
  unclear: "#CA8A04",
  unclearBg: "#FFFBEB",
  purple: Starling.purple,
  txt2: "#6B7280",
  txt3: "#9CA3AF",
} as const;

export type ClaimToastVariant = "warn";

export interface ClaimToastProps {
  variant: ClaimToastVariant;
  title: string;
  subtitle?: string;
  primary?: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
  onDismiss: () => void;
  autoDismissMs?: number;
  testId?: string;
}

export function ClaimToast({
  variant,
  title,
  subtitle,
  primary,
  secondary,
  onDismiss,
  autoDismissMs,
  testId,
}: ClaimToastProps) {
  useEffect(() => {
    if (!autoDismissMs) return;
    const handle = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(handle);
  }, [autoDismissMs, onDismiss]);

  if (typeof document === "undefined") return null;

  const toast = (
    <div
      data-testid={testId ?? "agent-claim-toast"}
      data-variant={variant}
      style={{
        background: TOKENS.card,
        borderRadius: 8,
        boxShadow: "0 10px 28px rgba(16,24,40,.18)",
        padding: "12px 14px",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        borderLeft: `3px solid ${TOKENS.unclear}`,
        maxWidth: 360,
        fontFamily: Starling.fontBody,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: TOKENS.unclearBg,
          color: TOKENS.unclear,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 700,
          flex: "none",
        }}
        aria-hidden
      >
        ⏱
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 12, color: TOKENS.txt2, lineHeight: 1.4 }}>{subtitle}</div>}
        {(primary || secondary) && (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            {primary && (
              <button
                type="button"
                onClick={primary.onClick}
                data-testid="agent-claim-toast-primary"
                style={{
                  background: TOKENS.purple,
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: 5,
                  fontFamily: Starling.fontBody,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {primary.label}
              </button>
            )}
            {secondary && (
              <button
                type="button"
                onClick={secondary.onClick}
                data-testid="agent-claim-toast-secondary"
                style={{
                  background: TOKENS.card,
                  border: `1px solid ${TOKENS.border}`,
                  color: "#111827",
                  padding: "6px 12px",
                  borderRadius: 5,
                  fontFamily: Starling.fontBody,
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {secondary.label}
              </button>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="agent-claim-toast-dismiss"
        style={{
          color: TOKENS.txt3,
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: 14,
          flex: "none",
        }}
      >
        ×
      </button>
    </div>
  );

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 1000,
      }}
    >
      {toast}
    </div>,
    document.body,
  );
}
